import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import stationManagement from '../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

interface ChartWidthInterface {
  type: 'percentage' | 'pixel';
  width: number;
}
/**
 * Custom square bar component
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => {}}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </>
  );
}

/**
 * HeatMapByHour
 * @returns {any} The rendered component
 */
function HeatMapByHour() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartWidth, setChartWidth] = useState<ChartWidthInterface>({
    type: 'percentage',
    width: 100,
  });
  const { selectedStationPerformanceList } = useAppSelector((state) => state.stationManagement);

  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <p className="label">
            {data?.label || data?.stationName
              ? t(data?.label || data?.stationName)
              : data?.label || data?.stationName}
          </p>
          <div className="tooltip-values">
            <p style={{ color: '#FF7714' }}>
              {t('durationHours')}: {data?.durationHours}
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on time range
   */
  const fetchPowerConsumedReport = async () => {
    setLoading(true);
    try {
      const response = await stationManagement.fetchHeatMapByHour({
        chargingStation: selectedStationPerformanceList?.id,
      });
      // Sort: higher durationHours → lower
      const sortedData = [...response.data].sort(
        (a: any, b: any) => b.durationHours - a.durationHours,
      );

      setBarGraphData(sortedData);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };
  useEffect(() => {
    fetchPowerConsumedReport();
  }, [selectedStationPerformanceList]);

  useEffect(() => {
    if (barGraphData.length <= 10) {
      setChartWidth({
        type: 'percentage',
        width: 100,
      });
    } else {
      setChartWidth({
        type: 'pixel',
        width: barGraphData.length * 50,
      });
    }
  }, [barGraphData]);
  return (
    <div className="station-status-distributionChart-wrap" dir={i18n.dir()}>
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <p>{t('heatMapByHourPerDay')} </p>
          </div>
        </div>
        {loading ? (
          <center className="loader">
            <BatteryLoader />
          </center>
        ) : (
          <div className="heatmap-by-hours-container">
            <div className="heatmap-by-hours-chart">
              <ResponsiveContainer
                width={chartWidth.type === 'pixel' ? chartWidth.width : `${chartWidth.width}%`}
                height={250}>
                <BarChart
                   data={barGraphData}
                   margin={{
                     left: i18n.dir() === 'rtl' ? 20 : 0,
                     right: i18n.dir() === 'rtl' ? 0 : 20,
                   }}>
                  <XAxis
                    reversed={i18n.dir() === 'rtl'}
                    dataKey="stationName" // Assuming 'label' has 'Jan', 'Feb', etc.
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) =>
                      value?.length > 3 ? `${value.slice(0, 3)}...` : value
                    }
                  />
                  <YAxis
                    orientation={i18n.dir() === 'rtl' ? 'right' : 'left'}
                    domain={[0, 24]}
                    tickFormatter={(value) => `${String(value).padStart(2, '0')}.00`}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#666', fontWeight: 600 }}
                    width={i18n.dir() === 'rtl' ? 65 : 35}
                    tickMargin={i18n.dir() === 'rtl' ? 25 : 0}
                    padding={{
                      top: 20,
                      bottom: 10,
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="durationHours"
                    name={t('durationHours')}
                    fill="#FF7714"
                    shape={<TriangleBar />}
                    barSize={30}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HeatMapByHour;
